import type { SyntheticEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useMenuNavigation from '../../hooks/useMenuNavigation/useMenuNavigation';
import type { IconColor } from '../../icons/types';
import type {
  ListSimpleDropdownItem,
  ListSimpleDropdownItems,
  NormalizedEntry,
  UseListSimpleDropdownParams,
  UseListSimpleDropdownReturn,
} from './types';

const DISABLED_ICON_COLOR: IconColor = 'grey-04';

/**
 * Normaliza los items públicos (lista plana o columnas 2D) a grupos de
 * entradas discriminadas (`separator` | `title` | `action`) con un índice
 * global consecutivo para la navegación por teclado. La API pública nunca ve
 * esta forma interna.
 */
function normalizeGroups(items: ListSimpleDropdownItems): NormalizedEntry[][] {
  const columns: ListSimpleDropdownItem[][] = Array.isArray(items[0])
    ? (items as ListSimpleDropdownItem[][])
    : [items as ListSimpleDropdownItem[]];

  let globalIndex = 0;
  return columns.map((column) =>
    column.map((source, positionInColumn) => {
      const currentIndex = globalIndex;
      globalIndex += 1;

      if (source.id === 'separator') {
        return { globalIndex: currentIndex, key: `separator-${currentIndex}`, kind: 'separator' };
      }

      const key = source.key ?? source.id ?? `entry-${currentIndex}-${positionInColumn}`;

      if (source.id === 'title') {
        return { globalIndex: currentIndex, key, kind: 'title', source };
      }

      return {
        globalIndex: currentIndex,
        isActive: Boolean(source.active),
        isDisabled: Boolean(source.disabled),
        key,
        kind: 'action',
        source,
      };
    }),
  );
}

// TAN-9539: los ids de las descripciones accesibles deben ser únicos entre
// instancias montadas a la vez (dos menús abiertos). React 17 no tiene useId.
let instanceCount = 0;

/**
 * Lógica del `ListSimpleDropdown`. Normaliza los items, conecta la navegación
 * por teclado (`useMenuNavigation`), reproduce la precedencia de color del v1
 * y despacha el `onClick` (prioridad componente sobre item), dejando el
 * componente como JSX puro.
 */
function useListSimpleDropdown({
  ariaLabel,
  colorIcon,
  colorIconActive,
  items,
  onClick,
  onCloseSubmenu,
  preventEvent,
  shouldAutoFocusFirstItem,
}: UseListSimpleDropdownParams): UseListSimpleDropdownReturn {
  const { t } = useTranslation();
  const resolvedAriaLabel = ariaLabel ?? t('common:Options');

  const instanceIdRef = useRef<number | null>(null);
  if (instanceIdRef.current === null) {
    instanceCount += 1;
    instanceIdRef.current = instanceCount;
  }

  // Id del `<span class="sr-only">` que expone el motivo de una fila deshabilitada:
  // el tooltip solo se abre con hover, así que sin esto el motivo es inalcanzable
  // por teclado y lector de pantalla (WCAG 2.1.1 / 1.3.1).
  const getDescriptionId = useCallback((key: string): string => `list-simple-dropdown-${instanceIdRef.current}-desc-${key}`, []);

  const isColumns = Array.isArray(items[0]);
  const groups = useMemo(() => normalizeGroups(items), [items]);
  const flat = useMemo(() => groups.flat(), [groups]);

  const isItemFocusable = useCallback((index: number) => flat[index]?.kind === 'action', [flat]);

  const isItemDisabled = useCallback((index: number) => {
    const entry = flat[index];
    return entry?.kind === 'action' && entry.isDisabled;
  }, [flat]);

  const isItemSubmenuTrigger = useCallback((index: number) => {
    const entry = flat[index];
    return entry?.kind === 'action' && Boolean(entry.source.hasSubmenu);
  }, [flat]);

  // Precedencia v1: item.color > item.colorIcon > colorIconActive (si activa) >
  // colorIcon (componente). Deshabilitada fuerza 'grey-04'.
  const getColor = useCallback(
    (source: ListSimpleDropdownItem): IconColor => {
      let color: string | undefined = source.colorIcon;
      if (source.color) {
        color = source.color;
      } else if (!color && colorIconActive && source.active) {
        color = colorIconActive;
      } else if (!color) {
        color = colorIcon;
      }
      return (source.disabled ? DISABLED_ICON_COLOR : color) as IconColor;
    },
    [colorIcon, colorIconActive],
  );

  const getRightIconColor = useCallback(
    (source: ListSimpleDropdownItem): IconColor => (colorIconActive && source.active ? colorIconActive : colorIcon) as IconColor,
    [colorIcon, colorIconActive],
  );

  // Despacho de la fila: reproduce `getOnClick` del v1 (preventEvent primero,
  // luego no-op si deshabilitada, prioridad del onClick de componente).
  const dispatchClick = useCallback(
    (source: ListSimpleDropdownItem, event: SyntheticEvent): void => {
      if (preventEvent) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (source.disabled) return;
      if (onClick) {
        onClick(source, event);
        return;
      }
      if (typeof source.onClick === 'function') {
        source.onClick(source, event);
      }
    },
    [onClick, preventEvent],
  );

  const onActivateItem = useCallback(
    (globalIndex: number, event: SyntheticEvent): void => {
      const entry = flat[globalIndex];
      if (entry?.kind === 'action') {
        dispatchClick(entry.source, event);
      }
    },
    [dispatchClick, flat],
  );

  const typeAheadLabels = useMemo(
    () => flat.map((entry) => (entry.kind === 'action' && typeof entry.source.name === 'string' ? entry.source.name : undefined)),
    [flat],
  );

  const { getItemProps: getMenuItemProps, getMenuProps } = useMenuNavigation({
    isItemDisabled,
    isItemFocusable,
    isItemSubmenuTrigger,
    itemCount: flat.length,
    onActivate: onActivateItem,
    onCloseSubmenu,
    shouldAutoFocusFirstItem,
    typeAheadLabels,
  });

  // Añade la activación por click y las props de submenú a las props ARIA de la
  // fila, para que el componente quede como JSX puro (click y Enter/Space
  // comparten despacho). `aria-haspopup`/`aria-expanded` viajan junto al
  // `role="menuitem"` del spread: sueltas en el `<li>` el analizador estático
  // asumiría el rol implícito `listitem`, que no las soporta.
  const getItemProps = useCallback(
    (globalIndex: number) => {
      function handleClick(event: SyntheticEvent): void {
        onActivateItem(globalIndex, event);
      }
      const entry = flat[globalIndex];
      const source = entry?.kind === 'action' ? entry.source : undefined;
      const submenuProps = source?.hasSubmenu ? { 'aria-expanded': Boolean(source.isSubmenuOpen), 'aria-haspopup': 'menu' as const } : {};
      return { ...getMenuItemProps(globalIndex), ...submenuProps, onClick: handleClick };
    },
    [flat, getMenuItemProps, onActivateItem],
  );

  return {
    getColor,
    getDescriptionId,
    getItemProps,
    getMenuProps,
    getRightIconColor,
    groups,
    isColumns,
    resolvedAriaLabel,
  };
}

export default useListSimpleDropdown;
